import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Polygon Popup Modal
 *
 * React modal for polygon/area operations on the map.
 * Replaces the legacy Bootstrap-based area.js popup.
 */
import React, { useState, useCallback } from 'react';
import { Save, RefreshCw, Layers, Trash2, AlertTriangle, ArrowLeft, X, Download } from 'lucide-react';
import { useModals } from '../../context/ModalContext';
import { useMap } from '../../context/MapContext';
import './polygon-popup-modal.css';
// Get userId from window.CFN
const getUserId = () => {
    return window.CFN?.userId;
};
// Get map reference for layer operations
const getMapFromWindow = () => {
    return window.map;
};
// Get layer by Leaflet ID
const getLayerById = (map, layerId) => {
    let foundLayer = null;
    map.eachLayer((layer) => {
        if (layer._leaflet_id === layerId) {
            foundLayer = layer;
        }
    });
    return foundLayer;
};
const ConfirmDialog = ({ title, message, onConfirm, onCancel, isDestructive = false, loading = false }) => (_jsxs("div", { className: "polygon-modal__confirm", children: [_jsxs("div", { className: "polygon-modal__confirm-header", children: [_jsx(AlertTriangle, { className: "h-5 w-5" }), _jsx("span", { children: title })] }), _jsx("p", { className: "polygon-modal__confirm-message", children: message }), _jsxs("div", { className: "polygon-modal__confirm-actions", children: [_jsx("button", { type: "button", className: "polygon-modal__btn polygon-modal__btn--secondary", onClick: onCancel, disabled: loading, children: "Cancel" }), _jsx("button", { type: "button", className: `polygon-modal__btn ${isDestructive ? 'polygon-modal__btn--danger' : 'polygon-modal__btn--primary'}`, onClick: onConfirm, disabled: loading, children: loading ? 'Please wait...' : 'Confirm' })] })] }));
const UPRNDownloadForm = ({ feature, onBack, onSuccess }) => {
    const [uprnType, setUprnType] = useState('all');
    const [format, setFormat] = useState('csv');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/uprn/fromPoly', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: uprnType,
                    format,
                    feature
                })
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch UPRNs: ${response.status} ${response.statusText}`);
            }
            const result = await response.text();
            const blob = new Blob([result]);
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = feature.properties['Area Name']
                ? `${feature.properties['Area Name']}-UPRNs.csv`
                : 'uprns.csv';
            link.click();
            onSuccess();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsxs("div", { className: "polygon-modal__form", children: [_jsxs("div", { className: "polygon-modal__form-header", children: [_jsx("button", { type: "button", className: "polygon-modal__back-btn", onClick: onBack, "aria-label": "Back", children: _jsx(ArrowLeft, { className: "h-4 w-4" }) }), _jsx("h3", { children: "Download UPRNs" })] }), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs("div", { className: "polygon-modal__field", children: [_jsx("label", { htmlFor: "uprn-type", children: "UPRN Type" }), _jsxs("select", { id: "uprn-type", value: uprnType, onChange: (e) => setUprnType(e.target.value), className: "polygon-modal__select", children: [_jsx("option", { value: "all", children: "All" }), _jsx("option", { value: "rural", children: "Rural" }), _jsx("option", { value: "omr_2021_white", children: "OMR 2021 White" }), _jsx("option", { value: "omr_2021_review", children: "OMR 2021 Review" })] })] }), _jsxs("div", { className: "polygon-modal__field", children: [_jsx("label", { htmlFor: "download-format", children: "Download Format" }), _jsxs("select", { id: "download-format", value: format, onChange: (e) => setFormat(e.target.value), className: "polygon-modal__select", children: [_jsx("option", { value: "csv", children: "CSV" }), _jsx("option", { value: "list", children: "UPRN List" })] })] }), error && _jsx("p", { className: "polygon-modal__error", children: error }), _jsx("button", { type: "submit", className: "polygon-modal__btn polygon-modal__btn--primary polygon-modal__btn--full", disabled: loading, children: loading ? 'Downloading...' : 'Download' })] })] }));
};
const CreateAreaForm = ({ feature, onBack, onSuccess }) => {
    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [shared, setShared] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/area/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name,
                    code,
                    shared,
                    feature
                })
            });
            if (!response.ok) {
                throw new Error(`Failed to create area: ${response.status} ${response.statusText}`);
            }
            await response.json();
            onSuccess();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsxs("div", { className: "polygon-modal__form", children: [_jsxs("div", { className: "polygon-modal__form-header", children: [_jsx("button", { type: "button", className: "polygon-modal__back-btn", onClick: onBack, "aria-label": "Back", children: _jsx(ArrowLeft, { className: "h-4 w-4" }) }), _jsx("h3", { children: "Create Area" })] }), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs("div", { className: "polygon-modal__field", children: [_jsx("label", { htmlFor: "area-name", children: "Name of Area" }), _jsx("input", { id: "area-name", type: "text", value: name, onChange: (e) => setName(e.target.value), className: "polygon-modal__input", pattern: "^[ 0-9a-zA-Z_-]{4,}$", required: true, autoComplete: "off" })] }), _jsxs("div", { className: "polygon-modal__field", children: [_jsx("label", { htmlFor: "area-code", children: "Optional 3 char code" }), _jsx("input", { id: "area-code", type: "text", value: code, onChange: (e) => setCode(e.target.value.toUpperCase()), className: "polygon-modal__input", pattern: "^[0-9A-Z]{3}$", maxLength: 3, autoComplete: "off" })] }), _jsxs("div", { className: "polygon-modal__field polygon-modal__field--checkbox", children: [_jsx("input", { id: "area-shared", type: "checkbox", checked: shared, onChange: (e) => setShared(e.target.checked), className: "polygon-modal__checkbox" }), _jsx("label", { htmlFor: "area-shared", children: "Share with others" })] }), error && _jsx("p", { className: "polygon-modal__error", children: error }), _jsx("button", { type: "submit", className: "polygon-modal__btn polygon-modal__btn--primary polygon-modal__btn--full", disabled: loading, children: loading ? 'Creating...' : 'Create Area' })] })] }));
};
const PolygonPopupModal = () => {
    const { isPolygonPopupOpen, polygonPopupData, closePolygonPopup } = useModals();
    const { getMap } = useMap();
    const [viewState, setViewState] = useState('main');
    const [confirmConfig, setConfirmConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [actionMessage, setActionMessage] = useState(null);
    const userId = getUserId();
    // Reset view state when modal opens/closes
    React.useEffect(() => {
        if (isPolygonPopupOpen) {
            setViewState('main');
            setConfirmConfig(null);
            setActionError(null);
            setActionMessage(null);
        }
    }, [isPolygonPopupOpen]);
    const removeLayer = useCallback(() => {
        if (!polygonPopupData)
            return;
        const map = getMap() || getMapFromWindow();
        if (map) {
            const layer = getLayerById(map, polygonPopupData.layerId);
            if (layer) {
                map.removeLayer(layer);
            }
        }
    }, [polygonPopupData, getMap]);
    const showConfirm = useCallback((config) => {
        setActionError(null);
        setActionMessage(null);
        setConfirmConfig(config);
        setViewState('confirm');
    }, []);
    const handleConfirm = useCallback(async () => {
        if (!confirmConfig)
            return;
        setLoading(true);
        try {
            const message = await confirmConfig.action();
            setActionMessage(message || null);
        }
        catch (err) {
            console.error('Action failed:', err);
            setActionMessage(null);
            setActionError(err instanceof Error ? err.message : 'The action failed. Please try again.');
        }
        finally {
            setLoading(false);
            setViewState('main');
            setConfirmConfig(null);
        }
    }, [confirmConfig]);
    const handleDiscard = useCallback(() => {
        if (!polygonPopupData)
            return;
        const isSaved = !!polygonPopupData.feature.properties.id;
        showConfirm({
            title: 'Discard Area',
            message: `Are you sure you want to discard this area${isSaved ? ' (Server data remains)' : ''}?`,
            action: async () => {
                removeLayer();
                closePolygonPopup();
            },
            isDestructive: true
        });
    }, [polygonPopupData, removeLayer, closePolygonPopup, showConfirm]);
    const handleDeleteArea = useCallback(() => {
        if (!polygonPopupData)
            return;
        showConfirm({
            title: 'Delete Area',
            message: 'Are you sure you want to delete this area from the server?',
            action: async () => {
                const response = await fetch(`/area/${polygonPopupData.feature.properties.id}`, { method: 'DELETE' });
                if (!response.ok) {
                    throw new Error(`Failed to delete: ${response.status}`);
                }
                const result = await response.json();
                if (result.result === 'OK') {
                    removeLayer();
                    closePolygonPopup();
                }
            },
            isDestructive: true
        });
    }, [polygonPopupData, removeLayer, closePolygonPopup, showConfirm]);
    const handleOpenreachDownload = useCallback(() => {
        if (!polygonPopupData)
            return;
        showConfirm({
            title: 'Download from Openreach',
            message: 'Are you sure you want to download from Openreach (1km x 1km resolution)?',
            action: async () => {
                const response = await fetch('/area/openreachDownload', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(polygonPopupData.feature)
                });
                const result = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(result.error || `Failed to queue Openreach download (${response.status})`);
                }
                if (result.result !== 'OK') {
                    throw new Error(result.error || 'Failed to queue Openreach download');
                }
                const queued = typeof result.queued === 'number' ? result.queued : null;
                return queued === null
                    ? 'Openreach download queued.'
                    : `${queued} Openreach tile${queued === 1 ? '' : 's'} queued.`;
            }
        });
    }, [polygonPopupData, showConfirm]);
    const handleRegenLeadins = useCallback(() => {
        if (!polygonPopupData)
            return;
        showConfirm({
            title: 'Regenerate Leadins',
            message: 'Are you sure you want to regenerate Leadins?',
            action: async () => {
                const response = await fetch('/leadin/regen', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(polygonPopupData.feature)
                });
                await response.json();
                // Refresh the inferred leadins layer
                const CFN = window.CFN;
                const layer = CFN?.getCachedLayer?.('inferredLeadInsLayer');
                if (layer) {
                    layer.setParams({ time: Date.now() }, false);
                }
            }
        });
    }, [polygonPopupData, showConfirm]);
    const handlePrepareArea = useCallback(() => {
        if (!polygonPopupData)
            return;
        showConfirm({
            title: 'Prepare Area',
            message: 'Are you sure you want to prepare the area?',
            action: async () => {
                const response = await fetch('/leadin/prepare', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(polygonPopupData.feature)
                });
                await response.json();
            }
        });
    }, [polygonPopupData, showConfirm]);
    if (!isPolygonPopupOpen || !polygonPopupData) {
        return null;
    }
    const { feature } = polygonPopupData;
    const properties = feature.properties;
    const isOwner = properties['User ID of creator'] === userId;
    const isSaved = !!properties.id;
    // Filter out null/undefined properties for display
    const displayProperties = Object.entries(properties).filter(([_, value]) => value !== null && value !== undefined);
    const renderContent = () => {
        if (viewState === 'uprn-download') {
            return (_jsx(UPRNDownloadForm, { feature: feature, onBack: () => setViewState('main'), onSuccess: closePolygonPopup }));
        }
        if (viewState === 'create-area') {
            return (_jsx(CreateAreaForm, { feature: feature, onBack: () => setViewState('main'), onSuccess: closePolygonPopup }));
        }
        if (viewState === 'confirm' && confirmConfig) {
            return (_jsx(ConfirmDialog, { title: confirmConfig.title, message: confirmConfig.message, onConfirm: handleConfirm, onCancel: () => {
                    setViewState('main');
                    setConfirmConfig(null);
                }, isDestructive: confirmConfig.isDestructive, loading: loading }));
        }
        return (_jsxs(_Fragment, { children: [actionError && (_jsx("p", { className: "polygon-modal__error", role: "alert", children: actionError })), actionMessage && (_jsx("p", { className: "polygon-modal__success", role: "status", children: actionMessage })), _jsxs("div", { className: "polygon-modal__actions", children: [_jsxs("button", { type: "button", className: "polygon-modal__action-btn", onClick: () => setViewState('uprn-download'), children: [_jsx(Download, { className: "h-4 w-4" }), _jsx("span", { children: "Download UPRNs" })] }), !isSaved && (_jsxs("button", { type: "button", className: "polygon-modal__action-btn", onClick: () => setViewState('create-area'), children: [_jsx(Save, { className: "h-4 w-4" }), _jsx("span", { children: "Save Area" })] })), _jsxs("button", { type: "button", className: "polygon-modal__action-btn", onClick: handleOpenreachDownload, children: [_jsx(Download, { className: "h-4 w-4" }), _jsx("span", { children: "Openreach Data" })] }), _jsxs("button", { type: "button", className: "polygon-modal__action-btn", onClick: handleRegenLeadins, children: [_jsx(RefreshCw, { className: "h-4 w-4" }), _jsx("span", { children: "Regen Lead-ins" })] }), _jsxs("button", { type: "button", className: "polygon-modal__action-btn", onClick: handlePrepareArea, children: [_jsx(Layers, { className: "h-4 w-4" }), _jsx("span", { children: "Prepare Area" })] }), isSaved && isOwner && (_jsxs("button", { type: "button", className: "polygon-modal__action-btn polygon-modal__action-btn--danger", onClick: handleDeleteArea, children: [_jsx(Trash2, { className: "h-4 w-4" }), _jsx("span", { children: "Delete Area" })] }))] }), displayProperties.length > 0 && (_jsx("div", { className: "polygon-modal__properties", children: _jsx("table", { className: "polygon-modal__table", children: _jsx("tbody", { children: displayProperties.map(([key, value]) => (_jsxs("tr", { children: [_jsx("th", { scope: "row", children: key }), _jsx("td", { children: String(value) })] }, key))) }) }) })), _jsx("div", { className: "polygon-modal__footer", children: _jsx("button", { type: "button", className: "polygon-modal__btn polygon-modal__btn--warning", onClick: handleDiscard, children: "Discard" }) })] }));
    };
    return (_jsx("div", { className: "polygon-modal__overlay", onClick: closePolygonPopup, children: _jsxs("div", { className: "polygon-modal__container", onClick: (e) => e.stopPropagation(), role: "dialog", "aria-modal": "true", "aria-labelledby": "polygon-modal-title", children: [_jsxs("div", { className: "polygon-modal__header", children: [_jsx("h2", { id: "polygon-modal-title", className: "polygon-modal__title", children: viewState === 'main' ? 'Area Options' : '' }), _jsx("button", { type: "button", className: "polygon-modal__close", onClick: closePolygonPopup, "aria-label": "Close", children: _jsx(X, { className: "h-5 w-5" }) })] }), _jsx("div", { className: "polygon-modal__content", children: renderContent() })] }) }));
};
export default PolygonPopupModal;
